import axios from 'axios';
import T from 'i18n-react';
import * as mdflavors_1 from 'i18n-react/dist/mdflavors';
import Debug from './Debug';
import Config from './Config';
import React from 'react';

var languages = {};
var localeSelected;
const MAESTRO_COMPONENT_NAME = 'daas_ui';

// Manually set locale
T.mt = (key, locale, variables) => {
  locale = (locale || '').replace(/_/g, '-');
  locale = langMapWithLocale(locale);
  if (!languages[locale] && Config.get('supportedLanguages').indexOf(locale) == -1) {
    locale = T.selectedLang;
  }
  localeSelected = locale;
  if (!key) {
    return '';
  }
  if (locale && languages[locale]) {
    T.setTexts(languages[locale]);
  } else {
    T.setTexts(languages[T.selectedLang]);
  }
  return localizeKeys(key, variables);
};

/**
 * Localize a key with optional variables and options.
 * @param {*} key
 * @param {*} variables
 * @param {*} opts - only one option 'ignoreMarkdown' is supported. If true, it will not parse markdown in the localized string.
 * @returns
 */
function localizeKeys(key, variables, opts) {
  if (!key) {
    return '';
  }
  T.texts = T.texts ? T.texts : {};
  let string = T.texts[key];
  if (variables && string && opts && opts.ignoreMarkdown) {
    return formatRaw(string, variables);
  } else if (variables && string) {
    return T.format(string, variables);
  } else {
    return string;
  }
}

/**
 * Function to format string without markdown parsing of i18n-react library. Code
 * is adapted from https://github.com/alexdrel/i18n-react/blob/master/src/mdflavors.ts
 * to maintain consistency in variable interpolation.
 */
function formatRaw(value, vars) {
  if (!value) return value;
  var matcher = /** @class */ (function () {
    function matcher(mdFlavor, inter, self) {
      this.mdFlavor = mdFlavor;
      this.inter = inter;
      this.self = self;
    }

    function flatten(l) {
      function isString(s) {
        return typeof s === 'string' || s instanceof String;
      }
      var r = [];
      var s = '';
      var flush = function () {
        return s && (r.push(s), (s = ''));
      };
      for (var _i = 0, l_1 = l; _i < l_1.length; _i++) {
        var i = l_1[_i];
        if (i == null) continue;
        if (isString(i)) {
          s += i;
        } else {
          flush();
          r.push(i);
        }
      }
      flush();
      return r.length > 1 ? r : r.length ? r[0] : null;
    }

    matcher.prototype.M = function (value) {
      if (!value) return null;
      var m = mdflavors_1.mdMatch(this.mdFlavor, value);
      if (!m) return value;
      let middle = null;
      switch (m.tag) {
        case 'inter':
          middle = this.inter && this.inter(m.body);
          break;
        case 'self':
          middle = this.self && this.self(m.body);
          break;
        case 'literals':
        case 'literal':
          middle = m.body;
          break;
        default:
          middle = React.createElement('span', { key: m.tag + m.body }, this.M(m.body));
          break;
      }
      return flatten([this.M(m.head), middle, this.M(m.tail)]);
    };
    return matcher;
  })();
  return new matcher(
    mdflavors_1.mdFlavors[L10n.MDFlavor],
    function (exp) {
      return L10n.interpolate(exp, vars);
    },
    function (exp) {
      return L10n.translate(exp, vars);
    },
  ).M(value);
}

export function langMapWithLocale(lang) {
  switch (lang) {
    case 'pt_BR':
    case 'pt_br':
    case 'pt-br':
      lang = 'pt-BR';
      break;
    case 'pt_pt':
    case 'pt_PT':
    case 'pt-pt':
      lang = 'pt-PT';
      break;
    case 'zh_CN':
    case 'zh_cn':
    case 'zh-cn':
      lang = 'zh-CN';
      break;
    case 'zh_HANS':
    case 'zh_hans':
    case 'zh-hans':
      lang = 'zh-HANS';
      break;
    case 'zh_TW':
    case 'zh_tw':
    case 'zh-tw':
      lang = 'zh-TW';
      break;
    default:
      break;
  }
  return lang;
}
T.__languages = languages;
T.__localeSelected = localeSelected;
T.t = (key, variables, opts) => {
  if (T.selectedLang !== localeSelected) {
    localeSelected = T.selectedLang;
    if (languages[localeSelected]) {
      T.setTexts(languages[T.selectedLang]);
    } else {
      loadLanguage(localeSelected).then(() => {
        T.setTexts(languages[T.selectedLang]);
      });
    }
    // T.setTexts(languages[T.selectedLang]);
  }
  if (!T.texts && Object.keys(languages).length) {
    T.setTexts(languages[Object.keys(languages)[0]]);
  }
  return localizeKeys(key, variables, opts);
};

var loadLanguage = (language, setLang = false) => {
  return new Promise(async (resolve, reject) => {
    language = (language || '').replace(/_/g, '-');
    language = langMapWithLocale(language);
    if (!language || Config.get('supportedLanguages').indexOf(language) == -1) {
      language = 'en';
    }

    if (language && languages[language]) {
      resolve();
      if (setLang) {
        Debug.log('[I18n] Setting language to: ' + language);
        T.selectedLang = language;
        T.setTexts(languages[language]);
        localStorage.setItem('language', language);
      }
      return;
    }

    // Will fetch locale file from CDN
    // For demo mode true:  will fetch from stable micro ui path (path in local config.json )
    // For demo mode false: will fetch from respective env.
    const localeBaseUrl = Config.get('localeBaseUrl');
    let targetUrl = `${localeBaseUrl}/${MAESTRO_COMPONENT_NAME}/${language}.json`;

    // load localization config.json to get build version
    const localeConfigPath = `${localeBaseUrl}/config.json`;
    try {
      const configRes = await axios.get(localeConfigPath);
      targetUrl = `${targetUrl}?v=${configRes.data.latestBuild}`;
    } catch (e) {
      Debug.log('I18n.loadLanguage - Exception thrown during load locale config: ' + e.message);
    }

    axios
      .get(targetUrl)
      .then((response) => {
        languages[language] = response.data;
        if (setLang) {
          T.selectedLang = language;
          T.setTexts(languages[language]);
          localStorage.setItem('language', language);
        }
        resolve();
      })
      .catch((e) => {
        Debug.log('I18n.loadLanguage - Exception thrown during dispatch: ' + e.message + '; targetUrl: ' + targetUrl);
        reject();
      });
  });
};
T.loadLanguage = loadLanguage;

window.L10n = T;
T.selectedLang = localStorage.getItem('language') || 'en';

export default (language) => {
  language = (language || '').replace(/_/g, '-');
  language = langMapWithLocale(language);
  return new Promise((resolve, reject) => {
    loadLanguage(language)
      .then(() => {
        T.selectedLang = language;
        T.setTexts(languages[language]);
        localStorage.setItem('language', language);
        resolve();
      })
      .catch(() => {
        if (language !== 'en') {
          loadLanguage('en');
        } else {
          reject();
        }
      });
  });
};
